/* [PATCH dobavnica-cmr-fe v1] Prodaja -> Nastavitve -> Dobavnica / CMR: vklop CMR v prodajnem toku (privzeto izključeno),
   privzeti izpis dobavnice s cenami / brez cen in stolpci izpisa (izbira + vrstni red). API: /izdaja/{cid}/nastavitve
   (backend nastavitve._preveri_dobavnica: znani stolpci, naziv vedno). Po shranjevanju sproži dogodek
   'izdaja-nastavitve-spremenjene' (meni pokaže / skrije CMR brez osvežitve strani). */
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Truck, Loader, Save, ChevronUp, ChevronDown } from 'lucide-react';
import { api } from '../../lib/api';

const STOLPCI: { k: string; l: string; cena?: boolean }[] = [
  { k: 'st', l: 'Zaporedna številka' }, { k: 'sifra', l: 'Šifra artikla' }, { k: 'naziv', l: 'Naziv (vedno)' },
  { k: 'kolicina', l: 'Količina' }, { k: 'enota', l: 'Enota' },
  { k: 'cena', l: 'Cena', cena: true }, { k: 'popust', l: 'Popust', cena: true }, { k: 'vrednost', l: 'Vrednost', cena: true },
  { k: 'neto_teza', l: 'Neto teža (kg)' }, { k: 'bruto_teza', l: 'Bruto teža (kg)' },
  { k: 'carinska_tarifa', l: 'Carinska tarifa' }, { k: 'drzava_porekla', l: 'Država porekla' },
];
const PRIVZETI_STOLPCI = ['st', 'sifra', 'naziv', 'kolicina', 'enota', 'neto_teza', 'bruto_teza'];
const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm disabled:opacity-50';
const btnP = btn + ' bg-blue-600 text-white border-blue-600 hover:bg-blue-700';

export function DobavnicaCmrKartica({ companyId }: { companyId?: string | null }) {
  const [nalozeno, setNalozeno] = useState(false);
  const [cmr, setCmr] = useState(false);
  const [sCenami, setSCenami] = useState(false);
  const [stolpci, setStolpci] = useState<string[]>(PRIVZETI_STOLPCI);
  const [shranjujem, setShranjujem] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/izdaja/${companyId}/nastavitve`).then(r => {
      const n = r.data?.nastavitve || {};
      setCmr(!!n.cmr_vklopljen);
      setSCenami(!!n.dobavnica?.s_cenami);
      const s: string[] = Array.isArray(n.dobavnica?.stolpci) && n.dobavnica.stolpci.length ? n.dobavnica.stolpci : PRIVZETI_STOLPCI;
      setStolpci(s.filter(k => STOLPCI.some(x => x.k === k)));
      setNalozeno(true);
    }).catch(() => setNalozeno(true));
  }, [companyId]);

  const preklopi = (k: string) => {
    if (k === 'naziv') return;
    setStolpci(s => (s.includes(k) ? s.filter(x => x !== k) : [...s, k]));
  };
  const premakni = (k: string, smer: -1 | 1) => setStolpci(s => {
    const i = s.indexOf(k), j = i + smer;
    if (i < 0 || j < 0 || j >= s.length) return s;
    const n = [...s]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const shrani = async () => {
    if (!companyId) return;
    setShranjujem(true);
    try {
      const r = await api.put(`/izdaja/${companyId}/nastavitve`, { cmr_vklopljen: cmr, dobavnica: { stolpci, s_cenami: sCenami } });
      const n = r.data?.nastavitve || {};
      if (Array.isArray(n.dobavnica?.stolpci)) setStolpci(n.dobavnica.stolpci);
      toast.success('Nastavitve dobavnice in CMR shranjene.');
      try { window.dispatchEvent(new Event('izdaja-nastavitve-spremenjene')); } catch { /* brez dogodka meni osveži ob naslednjem nalaganju */ }
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.sporocilo || 'Shranjevanje ni uspelo.'));
    } finally { setShranjujem(false); }
  };

  const izbrani = stolpci.map(k => STOLPCI.find(x => x.k === k)).filter(Boolean) as typeof STOLPCI;
  const ostali = STOLPCI.filter(x => !stolpci.includes(x.k));

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 max-w-4xl">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950 flex items-center justify-center text-blue-600 dark:text-blue-400"><Truck className="w-5 h-5" /></div>
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-white">Dobavnica in CMR</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Dobavnica in CMR ne gresta v knjigovodstvo in DDV evidenco — dobita samo zaporedno številko.</p>
        </div>
      </div>
      {!nalozeno ? (
        <div className="flex items-center justify-center h-16"><Loader className="w-5 h-5 text-blue-500 animate-spin" /></div>
      ) : (
        <div className="space-y-5">
          <section>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input type="checkbox" className="w-4 h-4" checked={cmr} onChange={e => setCmr(e.target.checked)} />
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">CMR tovorni list v prodajnem toku</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 pl-7">Vklopljeno: v meniju PRODAJNI TOK se pokaže »CMR«; CMR nastane iz dobavnice, iz CMR pa dobavnica ali račun. Izklopljeno (privzeto): CMR ni na voljo.</p>
          </section>
          <section>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input type="checkbox" className="w-4 h-4" checked={sCenami} onChange={e => setSCenami(e.target.checked)} />
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Dobavnico privzeto tiskaj s cenami</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 pl-7">Velja za PDF ob izdaji in pošiljanju. V dobavnici lahko vedno izbereš »PDF brez cen« ali »PDF s cenami«. Stolpci Cena, Popust in Vrednost se izpišejo samo s cenami.</p>
          </section>
          <section>
            <div className="text-sm font-semibold mb-2">Stolpci na dobavnici</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Izpisani — v tem vrstnem redu</div>
                <ul className="rounded-lg border dark:border-gray-700 divide-y dark:divide-gray-800">
                  {izbrani.map((s, i) => (
                    <li key={s.k} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                      <input type="checkbox" checked disabled={s.k === 'naziv'} onChange={() => preklopi(s.k)} />
                      <span className="flex-1">{s.l}{s.cena && <span className="text-xs text-gray-400"> · samo s cenami</span>}</span>
                      <button type="button" className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30" disabled={i === 0} onClick={() => premakni(s.k, -1)} title="Višje"><ChevronUp className="w-4 h-4" /></button>
                      <button type="button" className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30" disabled={i === izbrani.length - 1} onClick={() => premakni(s.k, 1)} title="Nižje"><ChevronDown className="w-4 h-4" /></button>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Na voljo</div>
                <ul className="rounded-lg border dark:border-gray-700 divide-y dark:divide-gray-800">
                  {ostali.length === 0 && <li className="px-3 py-1.5 text-sm text-gray-400">Vsi stolpci so izpisani.</li>}
                  {ostali.map(s => (
                    <li key={s.k} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                      <input type="checkbox" checked={false} onChange={() => preklopi(s.k)} />
                      <span className="flex-1 text-gray-600 dark:text-gray-300">{s.l}{s.cena && <span className="text-xs text-gray-400"> · samo s cenami</span>}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
          <div className="flex justify-end"><button className={btnP} onClick={shrani} disabled={shranjujem}><Save className="w-4 h-4" /> {shranjujem ? 'Shranjujem …' : 'Shrani'}</button></div>
        </div>
      )}
    </div>
  );
}
